/**
 * @module utils/scoreHelpers
 * @description
 *   Pure display helpers for analysis scores and trend demographics.
 *
 * @extracted-from client/src/App.js (lines ~1311–1355, Phase 7)
 */

/**
 * Number grade colors — lighter shade than Badge / sweet-spot accents so the
 * chip reads stronger (Technical Analysis + View).
 * ≥80 green · 70–79 yellow (Good) · 60–69 orange (Fair) · &lt;60 red (Poor).
 */
export function getScoreColor(score) {
  if (score === 'N/A') return 'text-critic-text-muted';
  if (score >= 80) return 'text-apple-green-soft';
  if (score >= 70) return 'text-apple-yellow-soft';
  if (score >= 60) return 'text-apple-orange-soft';
  return 'text-apple-error-soft';
}

export function getScoreLabel(score) {
  if (score === 'N/A') return 'Not Applicable';
  if (score >= 90) return 'Excellent';
  if (score >= 80) return 'Great';
  if (score >= 70) return 'Good';
  if (score >= 60) return 'Fair';
  return 'Poor';
}

/** Badge tone matching getScoreColor bands. */
export function getScoreBadgeTone(score) {
  if (score === 'N/A') return 'default';
  if (score >= 80) return 'success';
  if (score >= 70) return 'warning';
  if (score >= 60) return 'orange';
  return 'error';
}

/**
 * Hit potential number colors — same soft Apple ramp as craft grades
 * (no leftover blue). Soft text so the label badge reads stronger.
 */
export function getHitPotentialColor(score) {
  if (score >= 80) return 'text-apple-green-soft';
  if (score >= 65) return 'text-apple-yellow-soft';
  if (score >= 50) return 'text-apple-orange-soft';
  return 'text-apple-error-soft';
}

/** Badge tone for hit potential score (aligned with getHitPotentialColor). */
export function getHitPotentialBadgeTone(score) {
  if (score >= 80) return 'success';
  if (score >= 65) return 'warning';
  if (score >= 50) return 'orange';
  return 'error';
}

/** Age bracket display range for chart axes (en dash). */
export function formatAgeRange(ageGroup) {
  const rangeMap = {
    '13-17': '13–17',
    '18-24': '18–24',
    '25-34': '25–34',
    '35-44': '35–44',
    '45+': '45+',
  };
  return rangeMap[ageGroup] || ageGroup;
}

/**
 * Short demographic group name (tooltips / copy without the range).
 * 25–34 → “Adults”; 45+ → “Older adults” (standard demo label).
 */
export function formatAgeGroupName(ageGroup) {
  const nameMap = {
    '13-17': 'Teens',
    '18-24': 'Young adults',
    '25-34': 'Adults',
    '35-44': 'Midlife',
    '45+': 'Older adults',
  };
  return nameMap[ageGroup] || ageGroup;
}

/** Full label: "Young adults (18–24)". */
export function formatAgeGroup(ageGroup) {
  const name = formatAgeGroupName(ageGroup);
  if (name === ageGroup) return name;
  return `${name} (${formatAgeRange(ageGroup)})`;
}

/**
 * Marketing summary: "Young adult males (18–24) in North America".
 * Uses primary age + primary gender when binary; skips gender for other/missing.
 */
export function formatAudienceMarketPhrase({
  primaryTarget,
  primaryGender,
  primaryMarket,
} = {}) {
  if (!primaryTarget) return '';

  const range = formatAgeRange(primaryTarget);
  const ageName = formatAgeGroupName(primaryTarget);
  let who;

  if (primaryGender === 'male' || primaryGender === 'female') {
    const genderWord = primaryGender === 'male' ? 'males' : 'females';
    // "Young adults" → "Young adult males"; "Midlife" stays "Midlife males"
    const stem = /s$/i.test(ageName) ? ageName.replace(/s$/i, '') : ageName;
    who = `${stem} ${genderWord} (${range})`;
  } else {
    who = formatAgeGroup(primaryTarget);
  }

  if (primaryMarket) {
    return `Market to ${who} in ${formatMarket(primaryMarket)}.`;
  }
  return `Market to ${who}.`;
}

export function formatMarket(market) {
  const marketMap = {
    north_america: 'North America',
    europe: 'Europe',
    asia: 'Asia',
    latin_america: 'Latin America',
    africa: 'Africa',
  };
  return marketMap[market] || market;
}
